/**
 * AI Scribe V2 — INSTRUCTIONS TO SCRIBE column
 *
 * Free-text steering, an "always apply" preference and the terminal action
 * that stops listening and drafts the note.
 *
 * @author [Rajkumar Rathod]
 */

import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { IconButton, Switch, TextField } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import { SCRIBE_INSTRUCTIONS_LABELS } from "../../ai-scriber-constant";
import { SCRIBE_V2_COLORS } from "../constants";
import { ScribeInstructions } from "../types";
import ScribePanelHeader from "./ScribePanelHeader";

interface ScribeInstructionsPanelProps {
  instructions: ScribeInstructions;
  onChange: (next: ScribeInstructions) => void;
  /** Whether the text can be typed, edited or cleared (listening or paused). */
  isEditable: boolean;
  /** Fired when the provider applies the typed instruction to the running session */
  onApplyInstruction?: (text: string) => void;
  onFinish: () => void;
  isFinishDisabled?: boolean;
}

const fieldActionSx = {
  width: px(PRIMITIVE.spacing[24]),
  height: px(PRIMITIVE.spacing[24]),
  p: 0,
  backgroundColor: SCRIBE_V2_COLORS.actionIconBg,
  color: SCRIBE_V2_COLORS.actionIcon,
  "& svg": { fontSize: px(PRIMITIVE.spacing[16]) },
  "&:hover": { backgroundColor: SCRIBE_V2_COLORS.actionIconBg },
  /** Stay legible when idle — the mock shows both actions as solid chips */
  "&.Mui-disabled": {
    backgroundColor: SCRIBE_V2_COLORS.actionIconBg,
    color: SCRIBE_V2_COLORS.actionIcon,
    opacity: 0.6,
  },
} as const;

const ScribeInstructionsPanel = ({
  instructions,
  onChange,
  isEditable,
  onApplyInstruction,
  onFinish,
  isFinishDisabled,
}: ScribeInstructionsPanelProps) => (
  <Flex
    flexDirection="column"
    height="100%"
    sx={{
      minWidth: 0,
      flex: 1,
      borderLeft: `1px solid ${SCRIBE_V2_COLORS.panelDivider}`,
    }}
  >
    <ScribePanelHeader
      icon={<DescriptionOutlinedIcon />}
      label={SCRIBE_INSTRUCTIONS_LABELS.TITLE}
    />

    <Flex
      flexDirection="column"
      gap={1}
      p={px(PRIMITIVE.spacing[12])}
      sx={{ flex: 1, overflowY: "auto" }}
    >
      <Flex
        flexDirection="column"
        sx={{
          border: `1px solid ${SCRIBE_V2_COLORS.instructionsFieldBorder}`,
          borderRadius: px(PRIMITIVE.radius[4]),
          backgroundColor: SCRIBE_V2_COLORS.instructionsFieldBg,
        }}
      >
        <TextField
          multiline
          minRows={5}
          placeholder={SCRIBE_INSTRUCTIONS_LABELS.PLACEHOLDER}
          value={instructions.text}
          disabled={!isEditable}
          onChange={(event) =>
            onChange({ ...instructions, text: event.target.value })
          }
          variant="standard"
          InputProps={{ disableUnderline: true }}
          inputProps={{ "aria-label": SCRIBE_INSTRUCTIONS_LABELS.TITLE }}
          sx={{
            px: px(PRIMITIVE.spacing[8]),
            pt: px(PRIMITIVE.spacing[8]),
            "& .MuiInputBase-input": {
              fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
              color: SCRIBE_V2_COLORS.textPrimary,
            },
          }}
        />
        <Flex justifyContent="flex-end" gap={0.5} px={0.75} pb={0.75}>
          <IconButton
            size="small"
            title={SCRIBE_INSTRUCTIONS_LABELS.APPLY}
            aria-label={SCRIBE_INSTRUCTIONS_LABELS.APPLY}
            disabled={!isEditable || !instructions.text.trim()}
            onClick={() => onApplyInstruction?.(instructions.text.trim())}
            sx={fieldActionSx}
          >
            <SendOutlinedIcon />
          </IconButton>
          <IconButton
            size="small"
            title={SCRIBE_INSTRUCTIONS_LABELS.CLEAR}
            aria-label={SCRIBE_INSTRUCTIONS_LABELS.CLEAR}
            disabled={!isEditable || !instructions.text}
            onClick={() => onChange({ ...instructions, text: "" })}
            sx={fieldActionSx}
          >
            <RestartAltOutlinedIcon />
          </IconButton>
        </Flex>
      </Flex>

      <Flex alignItems="center" justifyContent="space-between" gap={1}>
        <Text
          sx={{
            color: SCRIBE_V2_COLORS.textPrimary,
            fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
          }}
        >
          {SCRIBE_INSTRUCTIONS_LABELS.ALWAYS_APPLY}
        </Text>
        <Switch
          size="small"
          checked={instructions.alwaysApply}
          inputProps={{ "aria-label": SCRIBE_INSTRUCTIONS_LABELS.ALWAYS_APPLY }}
          onChange={(_, checked) =>
            onChange({ ...instructions, alwaysApply: checked })
          }
        />
      </Flex>

      <StyledButtonV2
        variant="primary"
        label={SCRIBE_INSTRUCTIONS_LABELS.FINISH}
        sIcon={<AutoAwesomeOutlinedIcon />}
        disabled={isFinishDisabled}
        onClick={onFinish}
        sx={{ width: "100%", height: px(PRIMITIVE.spacing[32]) }}
      />
    </Flex>
  </Flex>
);

export default ScribeInstructionsPanel;
